import { get, mapValues } from "lodash";
import { getFormValues } from "redux-form";
import { createSelector } from "reselect";
import moment from "moment/moment";

import { Fields, PARCEL_EDIT_FORM } from "../../constants/forms";
import {
  ALTERNATIVE_DATE_FORMAT,
  DEFAULT_DATE_FORMAT,
} from "../../constants/dateFormats";
import { PARCEL_CODE } from "../../models/validators/regex";
import { getValue } from "../../utils/object";
import { formatDDMMYYYY } from "../../utils/date";
import { formatAddresses } from "./view/ReturnToSender/model";
import { findDocumentTypeByCode } from "./helpers";

export const getEditDelivery = state => state.ui.editDelivery;

const emptyArray = [];

export const getActionDates = createSelector(getEditDelivery, data =>
  getValue(data, "dates", emptyArray)
);

export const getCardDates = createSelector(getActionDates, dates =>
  dates.map(date => formatDDMMYYYY(date))
);

export const getDepot = createSelector(getEditDelivery, ({ depot }) => depot);

export const getNeighbourAddress = createSelector(
  getEditDelivery,
  ({ addresses }) => addresses
);

export const getParcelFormState = state =>
  state && state.form && state.form[PARCEL_EDIT_FORM];

export const getParcelFormStateValues = createSelector(
  getParcelFormState,
  state => state && state.values
);

export const getFoucusedParcelFormField = createSelector(
  getParcelFormState,
  state => state && state.active
);

export const getReturnAddresses = createSelector(getEditDelivery, data =>
  formatAddresses(getValue(data, "addresses", emptyArray))
);

export const getVaultDocumentTypesAndDims = createSelector(
  getEditDelivery,
  ({ vaultDocumentTypesAndDims }) => vaultDocumentTypesAndDims
);

export const getVaultDocumentTypesKeyValue = createSelector(
  getVaultDocumentTypesAndDims,
  vaultDocumentData => {
    if (!vaultDocumentData || typeof vaultDocumentData !== "object") {
      return [];
    }

    return Object.keys(vaultDocumentData).map(documentTypeName => ({
      label: documentTypeName,
      value: vaultDocumentData[documentTypeName].code,
    }));
  }
);

export const getSelectedVaultDocumentType = createSelector(
  getParcelFormStateValues,
  getVaultDocumentTypesAndDims,
  (formValues, vaultDocumentData) => {
    if (
      !formValues ||
      !vaultDocumentData ||
      typeof vaultDocumentData !== "object"
    ) {
      return null;
    }

    const selectedDocumentTypeCode = formValues[Fields.DOCUMENT_TYPE];
    if (!selectedDocumentTypeCode) {
      return null;
    }

    return findDocumentTypeByCode(vaultDocumentData, selectedDocumentTypeCode);
  }
);

export const getReturnKeyValueAddresses = createSelector(
  getReturnAddresses,
  addresses =>
    addresses.map((address, value) => ({
      label: address.deliveryAddressDescription,
      value,
    }))
);

export const getUpgradeDeliveryTimes = createSelector(
  getEditDelivery,
  ({ services }) => services
);

export const getAlternativeTimes = createSelector(
  getEditDelivery,
  ({ alternativeTimes }) =>
    alternativeTimes.map(value => ({
      label: value.upgradeDescription,
      value: value.upgradeId,
    }))
);

export const getPickupShops = createSelector(
  getEditDelivery,
  ({ shops }) => shops
);

export const getSelectedPickupShop = createSelector(
  getEditDelivery,
  ({ selectedPickupShop }) => selectedPickupShop
);

export const getParcelFormValues = getFormValues(PARCEL_EDIT_FORM);

export const getRelatedParcelValues = createSelector(
  getFormValues(PARCEL_EDIT_FORM),
  (values = null) => {
    const parcelCodes = {};
    if (values !== null) {
      mapValues(values, (value, key) => {
        if (PARCEL_CODE.test(key)) {
          parcelCodes[key] = value;
        }
      });
    }
    return parcelCodes;
  }
);

export const getIsMobileEmailRequired = createSelector(
  getParcelFormValues,
  values => {
    const mobile = get(values, Fields.PHONE, "");
    const email = get(values, Fields.EMAIL, "");
    return !mobile && !email;
  }
);

export const isDeliveryDateDisabled = createSelector(
  getParcelFormStateValues,
  formValues => formValues && !(formValues.street && formValues.city)
);

export const getIsAgreed = createSelector(getParcelFormStateValues, values =>
  get(values, Fields.AGREEMENT)
);

const dateStringToDate = (date, format = ALTERNATIVE_DATE_FORMAT) =>
  date && moment(date, format).toDate();

export const getAlternativeDateRange = createSelector(
  getActionDates,
  dates => ({
    minDate: dateStringToDate(getValue(dates, [0], null)),
    maxDate: dateStringToDate(getValue(dates, [dates.length - 1], null)),
  })
);

export const getDeliveryDateOpenToDate = createSelector(
  getFormValues(PARCEL_EDIT_FORM),
  getAlternativeDateRange,
  (values, dates) => {
    if (values && values[Fields.DELIVERY_DATE]) {
      return dateStringToDate(
        values[Fields.DELIVERY_DATE],
        DEFAULT_DATE_FORMAT
      );
    }
    return dates.minDate;
  }
);
